import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Download, ShieldAlert, Save, CalendarDays } from "lucide-react";

const STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DC", "DE", "FL", "GA", "HI", "IA", "ID",
  "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE",
  "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VA",
  "VT", "WA", "WI", "WV", "WY"];

const save = (blob, filename, type) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([blob], { type }));
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export function Form940Card({ year, enabled }) {
  const [preview, setPreview] = useState(null);
  const [deposits, setDeposits] = useState("0");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reports/940/preview?year=${year}&deposits=${Number(deposits || 0)}`);
      setPreview(data);
    } catch { setPreview(null); }
  }, [year, deposits]);
  useEffect(() => { if (enabled) load(); }, [load, enabled]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/reports/940.pdf", { year: Number(year), deposits: Number(deposits || 0) },
        { responseType: "blob" });
      save(res.data, `irs-940-${year}.pdf`, "application/pdf");
      await api.post("/reports/filings", { year: Number(year), quarter: 4, form: "940", method: "signable_pdf" });
      toast.success(`Form 940 · ${year} generated`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const row = (label, value, strong = false) => (
    <div className={`flex justify-between gap-4 font-mono text-xs py-1 border-b border-ink/15 ${strong ? "font-bold" : ""}`}>
      <span>{label}</span><span>{typeof value === "number" ? fmtMoney(value) : value}</span>
    </div>
  );

  return (
    <div className="hard-card p-6 mb-8" data-testid="form940-card">
      <div className="overline mb-2 flex items-center gap-2"><Landmark className="w-4 h-4" /> § ANNUAL FUTA</div>
      <div className="font-heading font-black text-2xl tracking-tight">Form 940 · {year}</div>
      <div className="font-mono text-xs text-muted-foreground mt-1 max-w-xl">
        Official IRS Form 940 filled on the government template: the $7,000 FUTA wage base per
        employee, the 0.6% net rate after the standard 5.4% state credit, and the Part 5 quarterly
        breakdown when tax runs over $500.
      </div>
      {preview && (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div data-testid="form940-lines">
            {row("3 · Total payments to employees", preview.line3_total_payments)}
            {row("4 · Payments exempt from FUTA", preview.line4_exempt)}
            {row(`5 · Payments over $${preview.futa_wage_base.toLocaleString()} per employee`, preview.line5_over_wage_base)}
            {row("7 · Total taxable FUTA wages", preview.line7_taxable_futa_wages)}
            {row("8 · FUTA tax @ 0.6%", preview.line8_futa_before_adjustments)}
            {row("11 · Credit reduction (Schedule A)", preview.line11_credit_reduction)}
            {row("12 · Total FUTA tax", preview.line12_total_futa, true)}
            {row("13 · Deposits", preview.line13_deposits)}
            {row("14 · Balance due", preview.line14_balance_due, true)}
            {row("15 · Overpayment", preview.line15_overpayment)}
            <div className="font-mono text-[10px] mt-2 opacity-70" data-testid="form940-note">
              {preview.line16_required
                ? "Over $500 — Part 5 quarterly liability is completed."
                : "$500 or less — Part 5 left blank; you may pay with the return."}
              {preview.line1b_multi_state ? " Multi-state employer box checked." : ""}
            </div>
            <div className="font-mono text-[10px] mt-1" data-testid="form940-schedule-a">
              {preview.schedule_a_required
                ? `Schedule A attached · ${preview.schedule_a.states_checked.join(", ")}`
                  + (preview.schedule_a.reduction_states.length
                    ? ` · credit reduction: ${preview.schedule_a.rows
                      .filter((r) => r.reduction_rate > 0)
                      .map((r) => `${r.state} @ ${(r.reduction_rate * 100).toFixed(1)}%`).join(", ")}`
                    : " · no credit-reduction state, filed because you are multi-state")
                : "Schedule A not required — single state, no credit reduction."}
              {preview.schedule_a && !preview.schedule_a.rates_determined
                ? ` The DOL has not published ${preview.year} credit-reduction states yet (determined each Nov 10), so none are applied.`
                : ""}
            </div>
          </div>
          <div>
            <div className="overline mb-2">FUTA deposited during the year</div>
            <input
              data-testid="form940-deposits" type="number" step="0.01" min="0" value={deposits}
              onChange={(e) => setDeposits(e.target.value)}
              className="w-full px-3 py-2 border-2 border-ink font-mono text-sm"
            />
            <button onClick={download} disabled={busy || !enabled} data-testid="form940-download"
              className="mt-4 w-full hard-btn py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40">
              <Download className="w-4 h-4" /> {busy ? "Generating…" : `Generate Form 940 · ${year}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function StateReturnCard({ year, enabled }) {
  const [state, setState] = useState("NY");
  const [quarter, setQuarter] = useState(1);
  const [uiRate, setUiRate] = useState("3.4");
  const [uiReg, setUiReg] = useState("");
  const [wtPaid, setWtPaid] = useState("0");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/reports/state-return/preview?year=${year}&quarter=${quarter}&state=${state}&ui_rate_pct=${Number(uiRate || 0)}`);
      setPreview(data);
    } catch { setPreview(null); }
  }, [year, quarter, state, uiRate]);
  useEffect(() => { if (enabled) load(); }, [load, enabled]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/reports/state-return.pdf", {
        state, year: Number(year), quarter, ui_rate_pct: Number(uiRate || 0),
        ui_registration: uiReg, wt_payments: Number(wtPaid || 0),
      }, { responseType: "blob" });
      save(res.data, `${state.toLowerCase()}-quarterly-Q${quarter}-${year}.pdf`, "application/pdf");
      toast.success(`${preview?.form || state} · Q${quarter} ${year} generated`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadWageFile = async () => {
    try {
      const res = await api.get(
        `/reports/state-return/wage-file.csv?year=${year}&quarter=${quarter}&state=${state}&ui_rate_pct=${Number(uiRate || 0)}`,
        { responseType: "blob" });
      save(res.data, `${state.toLowerCase()}-wage-file-Q${quarter}-${year}.csv`, "text/csv");
      toast.success("Wage listing exported — contains full SSNs, handle accordingly");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hard-card p-6 mb-8" data-testid="state-return-card">
      <div className="overline mb-2">§ STATE QUARTERLY RETURN</div>
      <div className="font-heading font-black text-2xl tracking-tight">
        {preview ? `${preview.form} · ${preview.agency}` : "State quarterly return"}
      </div>
      <div className="font-mono text-xs text-muted-foreground mt-1 max-w-xl">
        All 51 jurisdictions are covered. New York is keyed onto the state's own fillable NYS-45,
        Part C wage listing included; every other state gets a worksheet with that state's form
        number plus a wage-listing file for the agency's upload portal.
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-4 items-end">
        <div>
          <div className="overline mb-2 text-[10px]">State</div>
          <select data-testid="state-return-state" value={state} onChange={(e) => setState(e.target.value)}
            className="w-full px-3 py-2 border-2 border-ink font-mono text-sm">
            {STATES.map((s) => <option key={s} value={s} label={s} />)}
          </select>
        </div>
        <div>
          <div className="overline mb-2 text-[10px]">Quarter</div>
          <select data-testid="state-return-quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}
            className="w-full px-3 py-2 border-2 border-ink font-mono text-sm">
            {[1, 2, 3, 4].map((q) => <option key={q} value={q} label={`Q${q}`} />)}
          </select>
        </div>
        <div>
          <div className="overline mb-2 text-[10px]">Your UI rate %</div>
          <input data-testid="state-return-ui-rate" type="number" step="0.0001" min="0" value={uiRate}
            onChange={(e) => setUiRate(e.target.value)}
            className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
        </div>
        <button onClick={download} disabled={busy || !enabled} data-testid="state-return-download"
          className="hard-btn py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40">
          <Download className="w-4 h-4" /> {busy ? "Generating…" : "Generate"}
        </button>
      </div>
      {state === "NY" && (
        <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="ny-extra-inputs">
          <div>
            <div className="overline mb-2 text-[10px]">NY UI employer registration no.</div>
            <input data-testid="state-return-ui-registration" value={uiReg}
              onChange={(e) => setUiReg(e.target.value)} placeholder="1234567 8"
              className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </div>
          <div>
            <div className="overline mb-2 text-[10px]">Form NYS-1 payments already made</div>
            <input data-testid="state-return-wt-payments" type="number" step="0.01" min="0"
              value={wtPaid} onChange={(e) => setWtPaid(e.target.value)}
              className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </div>
        </div>
      )}
      {preview && (
        <div className="mt-4 grid gap-1" data-testid="state-return-lines">
          {[["Total remuneration paid", preview.total_remuneration],
            [`Wages over the UI base${preview.ui_wage_base ? ` ($${preview.ui_wage_base.toLocaleString()})` : ""}`, preview.wages_over_ui_base],
            ["Taxable wages", preview.taxable_wages],
            ["UI contributions due", preview.ui_contributions_due],
            ["State income tax withheld", preview.state_tax_withheld]].map(([l, v]) => (
              <div key={l} className="flex justify-between gap-4 font-mono text-xs py-1 border-b border-ink/15">
                <span>{l}</span><span>{fmtMoney(v)}</span>
              </div>
            ))}
          <div className="font-mono text-[10px] mt-2 opacity-70" data-testid="state-return-template-note">
            {preview.official_template
              ? `Filled on the state's official fillable template, including the Part C wage listing for ${preview.employees} employee(s)`
                + (preview.annual_columns
                  ? " with the 4th-quarter annual columns (gross federal wages, NYS/NYC/Yonkers withheld)."
                  : " (columns g-j are 4th-quarter only, per NY instructions).")
              : `${preview.agency} does not publish a fillable ${preview.form} — you'll get a worksheet with these figures and a wage-listing file for the portal.`}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            <button onClick={downloadWageFile} data-testid="state-wage-file-download"
              className="border-2 border-ink py-2 px-3 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white">
              <Download className="w-3 h-3" /> Wage listing (CSV)
            </button>
            {preview.efile_url && (
              <a href={preview.efile_url} target="_blank" rel="noreferrer"
                data-testid="state-efile-link"
                className="font-mono text-[10px] underline">File at {preview.agency} →</a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function CarrierRatesCard() {
  const [status, setStatus] = useState(null);
  const [text, setText] = useState("");
  const [carrier, setCarrier] = useState("");
  const [mod, setMod] = useState("1.00");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/wc/carrier-rates");
      setStatus(data);
      setCarrier(data.carrier_name || "");
      setMod(String(data.experience_mod ?? 1));
    } catch { setStatus(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wc/carrier-rates", {
        text, carrier_name: carrier, experience_mod: Number(mod || 1),
      });
      toast.success(`${data.accepted} carrier-filed rate(s) saved`
        + (data.rejected.length ? ` · ${data.rejected.length} line(s) skipped` : ""));
      setText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="hard-card p-6 mb-8" data-testid="carrier-rates-card">
      <div className="overline mb-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> § WORKERS' COMP · CARRIER RATES</div>
      <div className="font-heading font-black text-2xl tracking-tight">Carrier rate sheet</div>
      <div className="font-mono text-xs text-muted-foreground mt-1 max-w-xl">
        Paste your carrier's filed rates once — one <code>class code, rate</code> per line. They then
        override the indicative NCCI benchmark everywhere premiums are calculated, multiplied by
        your experience modification factor.
      </div>
      <div className="font-mono text-xs mt-3" data-testid="carrier-rates-status">
        {status?.count
          ? `${status.count} carrier-filed rate(s)${status.carrier_name ? ` · ${status.carrier_name}` : ""} · mod ${status.experience_mod} · updated ${String(status.updated_at || "").slice(0, 10)}`
          : "No carrier rates on file — premiums use the indicative NCCI benchmark."}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <div>
          <div className="overline mb-2 text-[10px]">Carrier</div>
          <input data-testid="carrier-name" value={carrier} onChange={(e) => setCarrier(e.target.value)}
            placeholder="Travelers" className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          <div className="overline mb-2 mt-3 text-[10px]">Experience mod (0.50–2.00)</div>
          <input data-testid="carrier-experience-mod" type="number" step="0.01" min="0.5" max="2" value={mod}
            onChange={(e) => setMod(e.target.value)}
            className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
        </div>
        <div className="md:col-span-2">
          <div className="overline mb-2 text-[10px]">Rate sheet</div>
          <textarea
            data-testid="carrier-rates-paste" rows={6} value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"8810, 0.19\n8742, 0.31\n5645, 11.50"}
            className="w-full px-3 py-2 border-2 border-ink font-mono text-xs"
          />
          <button onClick={submit} disabled={busy || !text.trim()} data-testid="carrier-rates-save"
            className="mt-2 hard-btn py-3 px-4 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save carrier rates"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DepositRemindersCard({ year }) {
  const [data, setData] = useState(null);
  const [cal, setCal] = useState(null);
  useEffect(() => {
    (async () => {
      try { setData((await api.get("/reports/deposit-schedule")).data); } catch { setData(null); }
      try { setCal((await api.get(`/reports/filing-calendar/summary?year=${year}`)).data); } catch { setCal(null); }
    })();
  }, [year]);

  const downloadIcs = async () => {
    try {
      const res = await api.get(`/reports/filing-calendar.ics?year=${year}`, { responseType: "blob" });
      save(res.data, `checkmate-filing-calendar-${year}.ics`, "text/calendar");
      toast.success(`${year} filing calendar exported — open it in Google, Apple or Outlook`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;
  return (
    <div className="hard-card p-6 mb-8" data-testid="deposit-reminders-card">
      <div className="overline mb-2">§ DEPOSIT CALENDAR · {data.deposit_schedule}</div>
      <div className="font-heading font-black text-2xl tracking-tight">Upcoming deposits & returns</div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button onClick={downloadIcs} data-testid="filing-calendar-download"
          className="hard-btn py-2 px-4 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2">
          <CalendarDays className="w-3 h-3" /> Export {year} filing calendar
        </button>
        {cal && (
          <span className="font-mono text-[10px] opacity-70" data-testid="filing-calendar-summary">
            {cal.events} deadline(s) · {cal.deposits} deposit(s) · {cal.returns} federal return(s)
            {cal.state_returns ? ` · ${cal.state_returns} ${cal.state} return(s)` : ""}
            {cal.first ? ` · ${cal.first} → ${cal.last}` : ""}
          </span>
        )}
      </div>
      {data.obligations.length === 0 ? (
        <div className="font-mono text-xs mt-3" data-testid="deposit-reminders-empty">
          Nothing due in the next {data.lookahead_days} days.
        </div>
      ) : (
        <div className="mt-4 border-2 border-ink" data-testid="deposit-reminders-list">
          {data.obligations.map((o) => (
            <div key={`${o.form}-${o.kind}-${o.due_date}`}
              className="flex items-center justify-between gap-3 border-b border-ink/20 last:border-b-0 px-3 py-2 font-mono text-xs">
              <span className="font-heading font-black">{o.label}</span>
              <span>{o.amount ? fmtMoney(o.amount) : "—"}</span>
              <span className={o.overdue ? "text-destructive font-bold" : ""}>
                {o.overdue ? "OVERDUE" : `${o.days_until}d`} · {o.due_date}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
